import { AxiosError } from 'axios';
import lang from '../languages/en.json';

/**
 * Interface for error notification configuration
 */
export interface ErrorNotificationConfig {
  /** Whether to show the error notification */
  isVisible: boolean;
  /** Error title/heading */
  title: string;
  /** Error message */
  message: string;
  /** Error severity level */
  severity: 'error' | 'warning' | 'info';
  /** HTTP status code */
  status?: number;
}

/**
 * Interface representing a standardized error response
 * @interface ErrorResponse
 */
export interface ErrorResponse {
  /** HTTP status code of the error */
  status: number;
  /** Optional status text description */
  statusText?: string;
  /** Human-readable error message */
  data: string;
}

/**
 * Type guard to check if an error is an AxiosError
 */
const isAxiosError = (err: unknown): err is AxiosError => {
  return (err as AxiosError).isAxiosError === true;
};

/**
 * Handles and standardizes Axios errors into a consistent ErrorResponse format
 *
 * @param error - The error to handle (can be AxiosError, Error, or any unknown value)
 * @returns Standardized error response object
 */
export const handleError = (error: unknown): ErrorResponse => {
  const { commonErrors } = lang;

  // Handle AxiosError with response (server responded with error status)
  if (isAxiosError(error) && error.response) {
    const { status, data } = error.response;
    const errMessage = (data as { ErrorMessage?: string }).ErrorMessage;

    const errorMessages: Record<number, string> = {
      400: commonErrors.error400,
      401: commonErrors.error401,
      404: commonErrors.error404,
      500: commonErrors.error500,
      503: commonErrors.error503,
    };

    return {
      status,
      data:
        typeof errMessage === 'string'
          ? errMessage
          : (errorMessages[status] ?? lang.errorNotification.defaultMessage),
    };
  } else if (isAxiosError(error) && error.request) {
    // Request was made but no response received - this is a real network error
    return {
      status: 0, // 0 specifically means network failure
      statusText: lang.errorMessages.titles.networkError,
      data: lang.errorMessages.networkErrorMessage,
    };
  }

  // Completely unknown error (not from axios)
  return {
    status: -1, // -1 means truly unknown
    data: error instanceof Error ? error.message : lang.errorNotification.defaultMessage,
  };
};

/**
 * Converts an ErrorResponse to a user-friendly notification configuration
 *
 * @param error - The error response object
 * @returns Configuration object for displaying error notification
 */
export const getErrorNotification = (error: ErrorResponse | null): ErrorNotificationConfig => {
  if (!error) {
    return {
      isVisible: false,
      title: '',
      message: '',
      severity: 'info',
    };
  }

  const { status, data } = error;

  // Determine severity and title based on status code
  let severity: 'error' | 'warning' | 'info' = 'error';
  let title = lang.errorMessages.titles.error;

  if (status === 403.1) {
    title = 'Session Expiring Soon';
    severity = 'warning';
  } else if (status === 404) {
    title = lang.errorMessages.titles.notFound;
    severity = 'warning';
  } else if (status === 401) {
    title = lang.errorMessages.titles.unauthorized;
    severity = 'warning';
  } else if (status === 403) {
    title = lang.errorMessages.titles.forbidden;
    severity = 'warning';
  } else if (status >= 500) {
    title = lang.errorMessages.titles.serverError;
    // severity already 'error'
  } else if (status >= 400) {
    title = lang.errorMessages.titles.requestError;
    severity = 'warning';
  } else if (status === 0) {
    title = lang.errorMessages.titles.networkError;
    // severity already 'error'
  }

  return {
    isVisible: true,
    title,
    message: data || lang.errorNotification.defaultMessage,
    severity,
    status,
  };
};

/**
 * Gets user-friendly error message with actionable suggestions
 *
 * @param error - The error response object
 * @returns User-friendly error message with suggestions
 */
export const getUserFriendlyErrorMessage = (error: ErrorResponse): string => {
  const { status, data } = error;

  const errorMessages: Record<number, string> = {
    400: lang.errorMessages.userFriendly['400'],
    401: lang.errorMessages.userFriendly['401'],
    403: lang.errorMessages.userFriendly['403'],
    404: lang.errorMessages.userFriendly['404'],
    500: lang.errorMessages.userFriendly['500'],
    503: lang.errorMessages.userFriendly['503'],
    0: lang.errorMessages.userFriendly['0'],
    [-1]: lang.errorMessages.userFriendly.unknown,
  };

  // Check if we have a predefined message for this status code
  if (status !== undefined && errorMessages[status]) {
    return errorMessages[status];
  }

  // Return the original message or a generic fallback
  return data || lang.errorMessages.userFriendly.unknown;
};
